import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiCreditCard, FiCalendar, FiDollarSign, FiClock, FiPlus, FiTrash2, FiCheck, FiX, FiAlertCircle, FiRefreshCw, FiChevronRight, FiLock, FiShield, FiEye, FiEyeOff, FiMail } from 'react-icons/fi';
import { supabase } from '../lib/supabase';
import authorizeNetRecurring from '../services/authorizeNetRecurring';

// Payment Portal Auth API
const AUTH_API_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/payment-portal-auth`;

const callAuthAPI = async (action, data = {}) => {
  const token = sessionStorage.getItem('paymentPortalToken');
  const response = await fetch(AUTH_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ action, ...data, token }),
  });
  return response.json();
};

const CustomerPaymentPortal = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [showAddCard, setShowAddCard] = useState(false);
  const [showMakePayment, setShowMakePayment] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [lookupForm, setLookupForm] = useState({ email: '' });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // OTP verification state
  const [authStep, setAuthStep] = useState('email'); // 'email' | 'otp' | 'authenticated'
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [otpExpiry, setOtpExpiry] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputRefs = useRef([]);

  const [cardForm, setCardForm] = useState({
    cardNumber: '', expirationDate: '', cvv: '', firstName: '', lastName: '',
    address: '', city: '', state: '', zip: '', isDefault: false
  });

  const [paymentForm, setPaymentForm] = useState({ amount: '', paymentMethodId: '' });

  // Check for existing valid session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      const savedToken = sessionStorage.getItem('paymentPortalToken');
      const savedEmail = sessionStorage.getItem('customerPaymentEmail');
      
      if (savedToken && savedEmail) {
        try {
          const result = await callAuthAPI('validate_session');
          if (result.success) {
            setLookupForm({ email: savedEmail });
            await loadCustomerData(savedEmail);
            setAuthStep('authenticated');
          } else {
            // Session expired, clear storage
            sessionStorage.removeItem('paymentPortalToken');
            sessionStorage.removeItem('customerPaymentEmail');
            setLoading(false);
          }
        } catch {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    checkExistingSession();
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const loadCustomerData = async (email) => {
    setLoading(true);
    try {
      const { data: profile } = await supabase
        .from('customer_payment_profiles')
        .select('*')
        .eq('customer_email', email)
        .single();

      if (profile) {
        setCustomerProfile(profile);
        setIsAuthenticated(true);
        sessionStorage.setItem('customerPaymentEmail', email);

        const { data: methods } = await supabase
          .from('payment_methods')
          .select('*')
          .eq('customer_profile_id', profile.id)
          .eq('is_active', true)
          .order('is_default', { ascending: false });
        setPaymentMethods(methods || []);

        const { data: subs } = await supabase
          .from('payment_subscriptions')
          .select('*')
          .eq('customer_profile_id', profile.id)
          .order('created_at', { ascending: false });
        setSubscriptions(subs || []);

        const { data: history } = await supabase
          .from('payment_history')
          .select('*')
          .eq('customer_profile_id', profile.id)
          .order('payment_date', { ascending: false })
          .limit(50);
        setPaymentHistory(history || []);
      } else {
        setMessage({ type: 'error', text: 'No account found. Contact us to set up your payment profile.' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Error loading account. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Request OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!lookupForm.email) {
      setMessage({ type: 'error', text: 'Please enter your email' });
      return;
    }
    
    setProcessing(true);
    setMessage({ type: '', text: '' });
    
    try {
      const result = await callAuthAPI('request_otp', { email: lookupForm.email });
      
      if (result.success) {
        setAuthStep('otp');
        setOtpExpiry(Date.now() + (result.expiresIn * 1000));
        setResendCooldown(60);
        setMessage({ type: 'success', text: 'Verification code sent to your email!' });
        // Focus first OTP input
        setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to send code' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setProcessing(false);
    }
  };

  // Handle OTP input
  const handleOTPChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only digits
    
    const newOtp = [...otpCode];
    newOtp[index] = value.slice(-1); // Only last digit
    setOtpCode(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when complete
    if (newOtp.every(d => d) && newOtp.join('').length === 6) {
      handleVerifyOTP(newOtp.join(''));
    }
  };

  // Handle OTP paste
  const handleOTPPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setOtpCode(newOtp);
      handleVerifyOTP(pastedData);
    }
  };

  // Handle OTP backspace
  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (code) => {
    setProcessing(true);
    setMessage({ type: '', text: '' });
    
    try {
      const result = await callAuthAPI('verify_otp', { 
        email: lookupForm.email, 
        code: code || otpCode.join('') 
      });
      
      if (result.success) {
        // Store session token
        sessionStorage.setItem('paymentPortalToken', result.token);
        sessionStorage.setItem('customerPaymentEmail', lookupForm.email);
        
        // Load customer data
        await loadCustomerData(lookupForm.email);
        setAuthStep('authenticated');
        setIsAuthenticated(true);
        setMessage({ type: 'success', text: 'Verified successfully!' });
      } else {
        setMessage({ type: 'error', text: result.error || 'Invalid code' });
        if (result.attemptsRemaining !== undefined) {
          setMessage({ type: 'error', text: `${result.error} (${result.attemptsRemaining + 1} attempts remaining)` });
        }
        // Clear OTP on error
        setOtpCode(['', '', '', '', '', '']);
        otpInputRefs.current[0]?.focus();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setProcessing(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    setProcessing(true);
    try {
      const result = await callAuthAPI('request_otp', { email: lookupForm.email });
      if (result.success) {
        setOtpExpiry(Date.now() + (result.expiresIn * 1000));
        setResendCooldown(60);
        setOtpCode(['', '', '', '', '', '']);
        setMessage({ type: 'success', text: 'New code sent!' });
        otpInputRefs.current[0]?.focus();
      } else {
        setMessage({ type: 'error', text: result.error });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to resend code' });
    } finally {
      setProcessing(false);
    }
  };

  // Go back to email step
  const handleBackToEmail = () => {
    setAuthStep('email');
    setOtpCode(['', '', '', '', '', '']);
    setMessage({ type: '', text: '' });
  };

  // Logout
  const handleLogout = async () => {
    try {
      await callAuthAPI('logout');
    } catch { /* Ignore logout errors */ }
    sessionStorage.removeItem('paymentPortalToken');
    sessionStorage.removeItem('customerPaymentEmail');
    setIsAuthenticated(false);
    setAuthStep('email');
    setCustomerProfile(null);
    setPaymentMethods([]);
    setSubscriptions([]);
    setPaymentHistory([]);
    setLookupForm({ email: '' });
    setOtpCode(['', '', '', '', '', '']);
  };

  const handleAddCard = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await authorizeNetRecurring.addPaymentProfile(
        customerProfile.authorize_customer_profile_id,
        { ...cardForm, phone: customerProfile.customer_phone }
      );

      if (result.success) {
        await supabase.from('payment_methods').insert({
          customer_profile_id: customerProfile.id,
          authorize_payment_profile_id: result.paymentProfileId,
          card_type: getCardType(cardForm.cardNumber),
          card_last_four: cardForm.cardNumber.slice(-4),
          card_expiration: cardForm.expirationDate,
          billing_name: `${cardForm.firstName} ${cardForm.lastName}`,
          billing_address: cardForm.address,
          billing_city: cardForm.city,
          billing_state: cardForm.state,
          billing_zip: cardForm.zip,
          is_default: cardForm.isDefault || paymentMethods.length === 0
        });
        setMessage({ type: 'success', text: 'Card added successfully!' });
        setShowAddCard(false);
        resetCardForm();
        loadCustomerData(customerProfile.customer_email);
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to add card' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error adding card' });
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteCard = async (method) => {
    if (!confirm('Remove this payment method?')) return;
    setProcessing(true);
    try {
      await authorizeNetRecurring.deletePaymentProfile(
        customerProfile.authorize_customer_profile_id,
        method.authorize_payment_profile_id
      );
      await supabase.from('payment_methods').update({ is_active: false }).eq('id', method.id);
      setMessage({ type: 'success', text: 'Card removed' });
      loadCustomerData(customerProfile.customer_email);
    } catch (error) {
      setMessage({ type: 'error', text: 'Error removing card' });
    } finally {
      setProcessing(false);
    }
  };

  const handleMakePayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount || !paymentForm.paymentMethodId) {
      setMessage({ type: 'error', text: 'Enter amount and select payment method' });
      return;
    }
    setProcessing(true);
    setMessage({ type: '', text: '' });

    try {
      const method = paymentMethods.find(m => m.id === paymentForm.paymentMethodId);
      const result = await authorizeNetRecurring.chargeCustomerProfile(
        customerProfile.authorize_customer_profile_id,
        method.authorize_payment_profile_id,
        parseFloat(paymentForm.amount),
        selectedSubscription?.name || 'One-time payment'
      );

      await supabase.from('payment_history').insert({
        customer_profile_id: customerProfile.id,
        subscription_id: selectedSubscription?.id,
        payment_method_id: method.id,
        authorize_transaction_id: result.transactionId,
        authorize_auth_code: result.authCode,
        transaction_type: selectedSubscription ? 'subscription' : 'payment',
        amount: parseFloat(paymentForm.amount),
        status: result.success ? 'success' : 'failed',
        card_type: method.card_type,
        card_last_four: method.card_last_four,
        description: selectedSubscription?.name || 'One-time payment',
        error_message: result.error
      });

      if (result.success) {
        setMessage({ type: 'success', text: `Payment of $${paymentForm.amount} successful!` });
        setShowMakePayment(false);
        setPaymentForm({ amount: '', paymentMethodId: '' });
        setSelectedSubscription(null);
        loadCustomerData(customerProfile.customer_email);
      } else {
        setMessage({ type: 'error', text: result.error || 'Payment failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Payment error. Try again.' });
    } finally {
      setProcessing(false);
    }
  };

  const resetCardForm = () => {
    setCardForm({ cardNumber: '', expirationDate: '', cvv: '', firstName: '', lastName: '', address: '', city: '', state: '', zip: '', isDefault: false });
  };

  const getCardType = (num) => {
    const c = num.replace(/\D/g, '');
    if (/^4/.test(c)) return 'visa';
    if (/^5[1-5]/.test(c)) return 'mastercard';
    if (/^3[47]/.test(c)) return 'amex';
    if (/^6/.test(c)) return 'discover';
    return 'card';
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amt || 0);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';

  const getStatusColor = (s) => ({
    active: 'bg-green-100 text-green-800', success: 'bg-green-100 text-green-800',
    paused: 'bg-yellow-100 text-yellow-800', pending: 'bg-yellow-100 text-yellow-800',
    canceled: 'bg-red-100 text-red-800', failed: 'bg-red-100 text-red-800',
    completed: 'bg-blue-100 text-blue-800', refunded: 'bg-purple-100 text-purple-800'
  }[s] || 'bg-gray-100 text-gray-800');

  const totalMonthly = subscriptions.filter(s => s.status === 'active').reduce((sum, s) => sum + parseFloat(s.amount || 0), 0);
  const totalPaid = paymentHistory.filter(p => p.status === 'success').reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

  if (!isAuthenticated) {
    return (
      <>
        <Helmet><title>Payment Portal | Paradise-Performance</title></Helmet>
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-6 text-white text-center">
                <FiShield className="w-12 h-12 mx-auto mb-3" />
                <h1 className="text-2xl font-bold">Payment Portal</h1>
                <p className="text-primary-100 text-sm mt-1">Secure access to your account</p>
              </div>
              
              {/* Step 1: Email Entry */}
              {authStep === 'email' && (
              <form onSubmit={handleRequestOTP} className="p-6 space-y-4">
                {message.text && (
                  <div className={`p-3 rounded-lg text-sm ${message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                    {message.text}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input type="email" value={lookupForm.email} onChange={(e) => setLookupForm({ email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="Enter your email" required />
                </div>
                <button type="submit" disabled={processing}
                  className="w-full bg-primary-600 text-white py-3 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {processing ? <><FiRefreshCw className="animate-spin" /> Sending code...</> : <>Send Verification Code <FiMail className="ml-1" /></>}
                </button>
                <p className="text-center text-sm text-gray-500 mt-4"><FiLock className="inline mr-1" />We'll send a 6-digit code to verify your identity</p>
              </form>
              )}

              {/* Step 2: OTP Verification */}
              {authStep === 'otp' && (
              <div className="p-6 space-y-4">
                {message.text && (
                  <div className={`p-3 rounded-lg text-sm ${message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                    {message.text}
                  </div>
                )}
                
                <div className="text-center">
                  <FiMail className="w-10 h-10 mx-auto text-primary-500 mb-2" />
                  <p className="text-gray-700 font-medium">Check your email</p>
                  <p className="text-gray-500 text-sm">We sent a code to <span className="font-medium">{lookupForm.email}</span></p>
                </div>

                <div className="flex justify-center gap-2">
                  {otpCode.map((digit, index) => (
                    <input
                      key={index}
                      ref={el => otpInputRefs.current[index] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOTPChange(index, e.target.value)}
                      onKeyDown={(e) => handleOTPKeyDown(index, e)}
                      onPaste={index === 0 ? handleOTPPaste : undefined}
                      className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all"
                      disabled={processing}
                    />
                  ))}
                </div>

                <button
                  onClick={() => handleVerifyOTP()}
                  disabled={processing || otpCode.join('').length !== 6}
                  className="w-full bg-primary-600 text-white py-3 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {processing ? <><FiRefreshCw className="animate-spin" /> Verifying...</> : <>Verify & Access Account <FiChevronRight /></>}
                </button>

                <div className="flex justify-between items-center text-sm">
                  <button onClick={handleBackToEmail} className="text-gray-500 hover:text-gray-700 flex items-center gap-1">
                    ← Change email
                  </button>
                  <button 
                    onClick={handleResendOTP} 
                    disabled={resendCooldown > 0 || processing}
                    className={`${resendCooldown > 0 ? 'text-gray-400' : 'text-primary-600 hover:text-primary-700'}`}
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                  </button>
                </div>

                <p className="text-center text-xs text-gray-400 mt-2">Code expires in 10 minutes</p>
              </div>
              )}
              <div className="bg-gray-50 px-6 py-4 border-t">
                <p className="text-center text-sm text-gray-600">Need help? <a href="tel:940-627-1455" className="text-primary-600 font-medium">(940) 627-1455</a></p>
              </div>
            </div>
          </motion.div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet><title>My Payments | Paradise-Performance</title></Helmet>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white">
          <div className="max-w-6xl mx-auto px-4 py-6 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Payment Portal</h1>
              <p className="text-gray-400 text-sm">Welcome, {customerProfile?.customer_name}</p>
            </div>
            <button onClick={handleLogout} className="text-gray-400 hover:text-white text-sm flex items-center gap-1"><FiX /> Sign Out</button>
          </div>
        </div>

        <div className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 flex gap-1 overflow-x-auto">
            {[{ id: 'overview', label: 'Overview', icon: FiDollarSign }, { id: 'methods', label: 'Payment Methods', icon: FiCreditCard },
              { id: 'subscriptions', label: 'Monthly Payments', icon: FiCalendar }, { id: 'history', label: 'History', icon: FiClock }].map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-4 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === tab.id ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                <tab.icon className="w-4 h-4" />{tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-6">
          <AnimatePresence>
            {message.text && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                {message.type === 'error' ? <FiAlertCircle /> : <FiCheck />}{message.text}
                <button onClick={() => setMessage({ type: '', text: '' })} className="ml-auto"><FiX /></button>
              </motion.div>
            )}
          </AnimatePresence>

          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-6 shadow-sm border">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><FiDollarSign className="w-5 h-5 text-green-600" /></div>
                    <span className="text-gray-500 text-sm">Monthly Payment</span>
                  </div>
                  <p className="text-3xl font-bold">{formatCurrency(totalMonthly)}</p>
                  <p className="text-sm text-gray-500">{subscriptions.filter(s => s.status === 'active').length} active</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm border">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><FiCreditCard className="w-5 h-5 text-blue-600" /></div>
                    <span className="text-gray-500 text-sm">Payment Methods</span>
                  </div>
                  <p className="text-3xl font-bold">{paymentMethods.length}</p>
                  <p className="text-sm text-gray-500">Card(s) on file</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm border">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><FiClock className="w-5 h-5 text-purple-600" /></div>
                    <span className="text-gray-500 text-sm">Total Paid</span>
                  </div>
                  <p className="text-3xl font-bold">{formatCurrency(totalPaid)}</p>
                  <p className="text-sm text-gray-500">{paymentHistory.filter(p => p.status === 'success').length} payments</p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <h3 className="font-semibold mb-4">Quick Actions</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <button onClick={() => { setShowMakePayment(true); setSelectedSubscription(null); }}
                    className="flex items-center gap-3 p-4 bg-primary-50 rounded-lg hover:bg-primary-100 text-left">
                    <FiDollarSign className="w-8 h-8 text-primary-600" />
                    <div><p className="font-medium">Make Payment</p><p className="text-sm text-gray-500">One-time</p></div>
                  </button>
                  <button onClick={() => setShowAddCard(true)} className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 text-left">
                    <FiCreditCard className="w-8 h-8 text-blue-600" />
                    <div><p className="font-medium">Add Card</p><p className="text-sm text-gray-500">New method</p></div>
                  </button>
                  <button onClick={() => setActiveTab('history')} className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg hover:bg-purple-100 text-left">
                    <FiClock className="w-8 h-8 text-purple-600" />
                    <div><p className="font-medium">View History</p><p className="text-sm text-gray-500">All payments</p></div>
                  </button>
                </div>
              </div>

              {subscriptions.filter(s => s.status === 'active').length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm border">
                  <h3 className="font-semibold mb-4">Upcoming Payments</h3>
                  <div className="space-y-3">
                    {subscriptions.filter(s => s.status === 'active').map((sub) => (
                      <div key={sub.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{sub.name}</p>
                          <p className="text-sm text-gray-500">Next: {formatDate(sub.next_payment_date)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">{formatCurrency(sub.amount)}</p>
                          <button onClick={() => { setSelectedSubscription(sub); setPaymentForm({ amount: sub.amount?.toString(), paymentMethodId: '' }); setShowMakePayment(true); }}
                            className="text-sm text-primary-600 hover:text-primary-700">Pay Now</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'methods' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Payment Methods</h2>
                <button onClick={() => setShowAddCard(true)} className="bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-700">
                  <FiPlus /> Add Card
                </button>
              </div>
              {paymentMethods.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center border">
                  <FiCreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No payment methods saved</p>
                  <button onClick={() => setShowAddCard(true)} className="mt-4 text-primary-600 font-medium">Add your first card</button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {paymentMethods.map((method) => (
                    <div key={method.id} className="bg-white rounded-xl p-6 border flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-10 bg-gradient-to-r from-gray-700 to-gray-900 rounded-lg flex items-center justify-center text-white text-xs font-bold">
                          {method.card_type?.toUpperCase().slice(0,4)}
                        </div>
                        <div>
                          <p className="font-medium">•••• •••• •••• {method.card_last_four}</p>
                          <p className="text-sm text-gray-500">Expires {method.card_expiration}</p>
                        </div>
                        {method.is_default && <span className="bg-primary-100 text-primary-700 text-xs px-2 py-1 rounded-full">Default</span>}
                      </div>
                      <button onClick={() => handleDeleteCard(method)} className="text-red-500 hover:text-red-700 p-2"><FiTrash2 /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'subscriptions' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Monthly Payments</h2>
              {subscriptions.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center border">
                  <FiCalendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No recurring payments set up</p>
                  <p className="text-sm text-gray-400 mt-2">Contact us to set up automatic monthly payments</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {subscriptions.map((sub) => (
                    <div key={sub.id} className="bg-white rounded-xl p-6 border">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="font-semibold text-lg">{sub.name}</h3>
                          <p className="text-sm text-gray-500">{sub.description}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(sub.status)}`}>{sub.status}</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div><p className="text-gray-500">Amount</p><p className="font-semibold">{formatCurrency(sub.amount)}/mo</p></div>
                        <div><p className="text-gray-500">Next Payment</p><p className="font-medium">{formatDate(sub.next_payment_date)}</p></div>
                        <div><p className="text-gray-500">Payments Made</p><p className="font-medium">{sub.payments_made || 0}</p></div>
                        <div><p className="text-gray-500">Started</p><p className="font-medium">{formatDate(sub.start_date)}</p></div>
                      </div>
                      {sub.status === 'active' && (
                        <div className="mt-4 pt-4 border-t">
                          <button onClick={() => { setSelectedSubscription(sub); setPaymentForm({ amount: sub.amount?.toString(), paymentMethodId: '' }); setShowMakePayment(true); }}
                            className="text-primary-600 font-medium text-sm hover:text-primary-700">Make Early Payment</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Payment History</h2>
              {paymentHistory.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center border">
                  <FiClock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No payment history yet</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl border overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Method</th>
                        <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {paymentHistory.map((payment) => (
                        <tr key={payment.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm">{formatDate(payment.payment_date)}</td>
                          <td className="px-6 py-4 text-sm">{payment.description || payment.transaction_type}</td>
                          <td className="px-6 py-4 text-sm">{payment.card_type} ••{payment.card_last_four}</td>
                          <td className="px-6 py-4 text-sm text-right font-medium">{formatCurrency(payment.amount)}</td>
                          <td className="px-6 py-4 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>{payment.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add Card Modal */}
        <AnimatePresence>
          {showAddCard && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b flex justify-between items-center">
                  <h2 className="text-xl font-bold">Add Payment Method</h2>
                  <button onClick={() => { setShowAddCard(false); resetCardForm(); }} className="text-gray-400 hover:text-gray-600"><FiX className="w-6 h-6" /></button>
                </div>
                <form onSubmit={handleAddCard} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
                    <input type="text" value={cardForm.cardNumber} onChange={(e) => setCardForm({ ...cardForm, cardNumber: e.target.value.replace(/\D/g, '').slice(0, 16) })}
                      className="w-full px-4 py-3 border rounded-lg" placeholder="1234 5678 9012 3456" required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Expiration</label>
                      <input type="text" value={cardForm.expirationDate} onChange={(e) => setCardForm({ ...cardForm, expirationDate: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg" placeholder="MM/YY" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CVV</label>
                      <input type="text" value={cardForm.cvv} onChange={(e) => setCardForm({ ...cardForm, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                        className="w-full px-4 py-3 border rounded-lg" placeholder="123" required />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <input type="text" value={cardForm.firstName} onChange={(e) => setCardForm({ ...cardForm, firstName: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <input type="text" value={cardForm.lastName} onChange={(e) => setCardForm({ ...cardForm, lastName: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg" required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Billing Address</label>
                    <input type="text" value={cardForm.address} onChange={(e) => setCardForm({ ...cardForm, address: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg" required />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input type="text" value={cardForm.city} onChange={(e) => setCardForm({ ...cardForm, city: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <input type="text" value={cardForm.state} onChange={(e) => setCardForm({ ...cardForm, state: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg" placeholder="TX" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                      <input type="text" value={cardForm.zip} onChange={(e) => setCardForm({ ...cardForm, zip: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg" required />
                    </div>
                  </div>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={cardForm.isDefault} onChange={(e) => setCardForm({ ...cardForm, isDefault: e.target.checked })} className="rounded" />
                    <span className="text-sm text-gray-600">Set as default payment method</span>
                  </label>
                  <button type="submit" disabled={processing} className="w-full bg-primary-600 text-white py-3 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2">
                    {processing ? <><FiRefreshCw className="animate-spin" /> Adding...</> : <><FiPlus /> Add Card</>}
                  </button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Make Payment Modal */}
        <AnimatePresence>
          {showMakePayment && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-2xl max-w-md w-full">
                <div className="p-6 border-b flex justify-between items-center">
                  <h2 className="text-xl font-bold">{selectedSubscription ? `Pay ${selectedSubscription.name}` : 'Make a Payment'}</h2>
                  <button onClick={() => { setShowMakePayment(false); setSelectedSubscription(null); setPaymentForm({ amount: '', paymentMethodId: '' }); }} className="text-gray-400 hover:text-gray-600"><FiX className="w-6 h-6" /></button>
                </div>
                <form onSubmit={handleMakePayment} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                    <div className="relative">
                      <span className="absolute left-4 top-3 text-gray-500">$</span>
                      <input type="number" step="0.01" min="1" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                        className="w-full pl-8 pr-4 py-3 border rounded-lg" placeholder="0.00" required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    {paymentMethods.length === 0 ? (
                      <p className="text-sm text-gray-500">No payment methods. <button type="button" onClick={() => { setShowMakePayment(false); setShowAddCard(true); }} className="text-primary-600">Add one</button></p>
                    ) : (
                      <div className="space-y-2">
                        {paymentMethods.map((method) => (
                          <label key={method.id} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${paymentForm.paymentMethodId === method.id ? 'border-primary-500 bg-primary-50' : 'hover:bg-gray-50'}`}>
                            <input type="radio" name="paymentMethod" value={method.id} checked={paymentForm.paymentMethodId === method.id}
                              onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethodId: e.target.value })} className="text-primary-600" />
                            <span className="font-medium">{method.card_type} •••• {method.card_last_four}</span>
                            {method.is_default && <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">Default</span>}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <button type="submit" disabled={processing || paymentMethods.length === 0}
                    className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2">
                    {processing ? <><FiRefreshCw className="animate-spin" /> Processing...</> : <><FiDollarSign /> Pay {paymentForm.amount ? formatCurrency(parseFloat(paymentForm.amount)) : ''}</>}
                  </button>
                  <p className="text-center text-xs text-gray-500"><FiLock className="inline mr-1" />Secure payment via Authorize.Net</p>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default CustomerPaymentPortal;
